import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import {
  setHISCardSelected,
  setPatientSearchDetail,
  getPatientVisitDetails,
  setIsHISClaim,
  setAutomatedNewAdmission,
} from '../../../../../../store/actions/hospitalAction';
import { setPolicySearchResults, setSelectedPolicyDetail } from '../../../../../../store/actions/nhcxAction';
import { ApplicationDetailModel } from '../../../../../../store/models/applicationDetailModel';
import { HospitalState } from '../../../../../../store/models/hospitalModel';
// import { LoadingOutlined} from '@ant-design/icons';
import { isEmpty } from 'lodash';
import HISCardRedesign from '../cards/BeneficiaryCard/HISCardRedesign';
import { centeredNotification } from '../../../../../../utils/utility';
import {ReactComponent as HIS_View_all} from '../../../../../../assets/images/svg/view-all-plus.svg';
import {ReactComponent as HIS_Hide_all} from '../../../../../../assets/images/svg/hide-all-minus.svg';
import ApplicationDetail from '../../../../../../shared/services/applicationDetail.service';

const HISSearchCards: any = (props: any): any => {
  const { form, setDefaultPayer, dataFound, loading, setSelectedOption,
    selectedOption, completeStep, resetMultipleSteps, isHISVisitError } = props;
  const dispatch = useDispatch();
  const { selectedPatientDetail, hospitalSearchPatientDetails, hospitalSearchValues } =
  useSelector((state: RootState): HospitalState => state.hospital);
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const isNHCXEnabledProvider = ApplicationDetail.instance.getIsNHCXEnabledProvider();
  const { isVisitsEnabled, hisid } = appDetail?._applicationDetail?.hospitalDetail?.providerConfigurableProperties;
  const [tempBeneficiaryIndex, setTempBeneficiaryIndex] = useState<any>(-1);
  const [tempBeneficiary, setTempBeneficiary] = useState<any>(null);
  const [nextClicked, setNextClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowAllCards, setIsShowAllCards] = useState(false);
  const [isError, setIsError] = useState(false);
  const isMountedRef = useRef(true);

  // Common state update function
  const updateCommonState = (isSuccess: boolean): void => {
    setNextClicked(isSuccess);
    dispatch(setHISCardSelected(isSuccess));
    dispatch(setIsHISClaim(isSuccess));
    setIsLoading(false);
    completeStep('patientCard');
    isHISVisitError(!isSuccess);
    setIsError(!isSuccess);
  };
  const setClaimFlowType = (): void => {
    isNHCXEnabledProvider ? setSelectedOption('NHCX') : setSelectedOption('insurance');
    isNHCXEnabledProvider && dispatch(setAutomatedNewAdmission(false));
  };

  const onSuccess = (hospPatientDetails: Record<string, any>): void => {
    // Only proceed if component is still mounted
    if (!isMountedRef.current) {
      return;
    }
    dispatch(setPatientSearchDetail(hospPatientDetails));
    updateCommonState(true);
    setClaimFlowType();
  };

  const onError = (e: any): void => {
    // Only proceed if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    updateCommonState(false);
    if (nextClicked) {
      dispatch(setPatientSearchDetail([]));
    }
    dispatch(setSelectedPolicyDetail([]));
    form?.resetFields(['payer']);
    setDefaultPayer();
    setClaimFlowType();
    centeredNotification(e);
  };

  // Prepare reset function to avoid code duplication
  const resetSelectionState = (): void => {
    setTempBeneficiaryIndex(null);
    setTempBeneficiary(null);
    setIsShowAllCards(false);
    setNextClicked(false);
    setIsLoading(false);
    setSelectedOption('');

    // Dispatch reset actions
    dispatch(setPatientSearchDetail([]));
    dispatch(setPolicySearchResults([]));
    dispatch(setSelectedPolicyDetail([]));
    dispatch(setHISCardSelected(false));
    dispatch(setIsHISClaim(false));
    setDefaultPayer();
    form.resetFields(['payer']);

    // Reset step
    resetMultipleSteps(['patientCard', 'memberOrPolicy',
      'policyCard', 'memberSearch', 'claimReviewed',
      'documentsAttached', 'submitClaim', 'memberCard',
    ]);
  };

  const handleCardClick = (beneficiary: any, index: number): void => {
    // Check if the clicked card is the currently selected card
    const isClickingSelectedCard =
      !isEmpty(selectedPatientDetail) &&
      tempBeneficiary === hospitalSearchPatientDetails[index];

    // If clicking the same card, unselect everything
    if (isClickingSelectedCard) {
      // Reset all selection-related states
      resetSelectionState();
      return;
    }

    // Reset previous states before selecting a new card
    // This ensures a clean slate for each card selection
    resetSelectionState();
    // Now proceed with new card selection
    setTempBeneficiaryIndex(index);
    setTempBeneficiary(beneficiary);
    setIsLoading(true);
    dispatch(setHISCardSelected(true));
    isHISVisitError(false);
    setIsError(false);

    if (isVisitsEnabled) {
      const HISVisitsPayload = {
        ...beneficiary,
        ...hospitalSearchValues,
        'loggedInUserId': appDetail?._applicationDetail?.userDetail?.userId,
        'hisMasterID': appDetail?._applicationDetail?.hospitalDetail?.providerConfigurableProperties?.hisid,
        'hisProviderID': appDetail?._applicationDetail?.hospitalDetail?.providerConfigurableProperties?.hisProviderID,
      };
      dispatch(getPatientVisitDetails(HISVisitsPayload, onSuccess, onError, isVisitsEnabled));
    } else {
      dispatch(setPatientSearchDetail(beneficiary));
      setNextClicked(true);
      dispatch(setHISCardSelected(true));
      dispatch(setIsHISClaim(true));
      setClaimFlowType();
      setIsLoading(false);
      completeStep('patientCard');
    }
  };

  const handleShowAllCards = (): void => {
    setIsShowAllCards(!isShowAllCards);
  };

  useEffect((): any => {
    if (dataFound && hospitalSearchPatientDetails && hospitalSearchPatientDetails.length === 1) {
      const timer = setTimeout((): void => {
        handleCardClick(hospitalSearchPatientDetails[0], 0);
      }, 250);

      // Cleanup function to clear the timer if the component unmounts or dependencies change
      return (): void => clearTimeout(timer);
    }
  }, [dataFound, hospitalSearchPatientDetails]);

  // Cleanup effect to handle component unmount or navigation away
  useEffect((): (() => void) => {
    return (): void => {
      // Mark component as unmounted
      isMountedRef.current = false;
    };
  }, []);

  if (!dataFound && !loading && selectedOption === '') {
    return (
      <Row className="HIS-error-message">
        <p>No matching patients in HIS. Verify details or submit via Insurance/NHCX below</p>
      </Row>
    );
  }

  return (
    <>
      <Row gutter={[16, 16]}>
        {((!isEmpty(selectedPatientDetail) && !isShowAllCards) ? [hospitalSearchPatientDetails?.[tempBeneficiaryIndex]] :
        hospitalSearchPatientDetails)
          ?.map((beneficiary: any, mapIndex: number): React.ReactElement => {
            // Use the original index from hospitalSearchPatientDetails instead of the map index
            const originalIndex = !isEmpty(selectedPatientDetail) && !isShowAllCards
              ? tempBeneficiaryIndex
              : mapIndex;

            return (
              <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={8} key={originalIndex}
                onClick={(): void => handleCardClick(beneficiary, originalIndex)}
              >
                <HISCardRedesign
                  HISPatient={beneficiary}
                  index={originalIndex}
                  isLoading={isLoading}
                  cardSelected={(tempBeneficiary === hospitalSearchPatientDetails?.[originalIndex] && !isError)}
                  hisId = {hisid}
                />
              </Col>
            );
          })}
      </Row>
      {!isEmpty(selectedPatientDetail) && (hospitalSearchPatientDetails?.length > 1) && (
        <Row>
          <Col>
            <Button
              type="link"
              icon={
                isShowAllCards ? (
                  <HIS_Hide_all className="hide-cards" />
                ) : (
                  <HIS_View_all/>
                )
              }
              onClick={handleShowAllCards}
              className="show-all-button"
            >
              <span className="view-all-text">
                {isShowAllCards ? 'Hide Other Visit Cards' : 'View All Visit Cards'}
              </span>
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default HISSearchCards;
